import { includes, isEmpty, map } from 'lodash';
import React from 'react';
import { ResponseError } from 'superagent';

import {
  BulkSlideTagSelectionSlide,
  BulkSlideTagOperation,
  BulkSlideTagRequest,
  BulkSlideTagResultScopeMode,
  BulkSlideTagSummary,
} from 'api/slideTags';
import { ProcedureResponse } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { SlideTag } from 'interfaces/slideTag';
import { CasesParams } from 'utils/useCasesParams/types';
import { TAG_VALUE_OPERATIONS } from './constants';

export const hasExplicitSlideSelection = (casesParams: CasesParams) => !isEmpty(casesParams.slideIdsToInclude);

export const hasTargetedSlideSelection = (casesParams: CasesParams) =>
  hasExplicitSlideSelection(casesParams) || casesParams.slideIdsToExclude !== undefined;

export const operationRequiresTags = (operation: BulkSlideTagOperation) => includes(TAG_VALUE_OPERATIONS, operation);

export const hasExperimentResultSpecificTags = (tags: SlideTag[]) => tags.some((tag) => tag.perExperimentResult);

export const getBulkSlideTagErrorMessage = (error: unknown) => {
  const responseError = error as ResponseError | undefined;
  return (
    responseError?.response?.body?.message ||
    responseError?.response?.body?.detail ||
    responseError?.message ||
    'Bulk slide tagging failed.'
  );
};

export const useBulkSlideTagRequest = ({
  casesParams,
  operation,
  selectedTags,
  resultScopeMode,
  orchestrationId,
}: {
  casesParams: CasesParams;
  operation: BulkSlideTagOperation;
  selectedTags: SlideTag[];
  resultScopeMode: BulkSlideTagResultScopeMode;
  orchestrationId?: string;
}): BulkSlideTagRequest =>
  React.useMemo(
    () => ({
      ...casesParams,
      operation,
      tagIds: map(selectedTags, 'id'),
      resultScopeMode,
      ...(resultScopeMode === 'explicit' && orchestrationId ? { orchestrationId } : {}),
    }),
    [casesParams, operation, orchestrationId, resultScopeMode, selectedTags]
  );

export const getPreviewSlide = (slide: BulkSlideTagSummary['previewSlides'][number]): Slide => ({
  id: slide.slideId,
  labId: slide.labId || undefined,
  originalFileName: slide.originalFileName || undefined,
  stainingType: slide.stainingType || undefined,
});

export const getSlideFromSelection = (slide: BulkSlideTagSelectionSlide): Slide => ({
  id: slide.slideId,
  labId: slide.labId || undefined,
  originalFileName: slide.originalFileName || undefined,
  stainingType: slide.stainingType || undefined,
  negativeControl: slide.negativeControl || undefined,
  positiveControl: slide.positiveControl || undefined,
  slideTagAssignments: map(slide.slideTagAssignments || [], (assignment) => ({
    tagId: assignment.tagId,
    studyId: assignment.studyId || undefined,
    orchestrationId: assignment.orchestrationId || undefined,
  })),
});

export const getHydratedSelectedSlidesById = ({
  selectedSlides,
  procedureResponses,
}: {
  selectedSlides: Slide[];
  procedureResponses: Array<ProcedureResponse | undefined>;
}) => {
  const selectedSlideIds = new Set(map(selectedSlides, 'id'));
  const slidesById = selectedSlides.reduce<Record<string, Slide>>((nextSlidesById, slide) => {
    nextSlidesById[slide.id] = slide;
    return nextSlidesById;
  }, {});

  procedureResponses.forEach((procedureResponse) => {
    procedureResponse?.procedures?.forEach((procedure) => {
      procedure?.slides?.forEach((slide) => {
        if (selectedSlideIds.has(slide.id)) {
          slidesById[slide.id] = {
            ...slidesById[slide.id],
            ...slide,
          };
        }
      });
    });
  });

  return slidesById;
};
